import { useTranslation } from 'react-i18next';

import type { ComboboxOption } from '../../components/Combobox';
import { CustomerCombobox } from '../crm/CustomerCombobox';
import { customerOption } from '../crm/customerOption';
import { useCustomer, useCustomerList } from '../crm/queries';

interface TicketCustomerFilterProps {
  /** Die Kennung aus der Adresse, leer ohne Auswahl. */
  customerId: string;
  onChange: (customerId: string | undefined) => void;
}

/**
 * Die Kundenauswahl des Ticketmoduls -- F1-56.
 *
 * Steht ueber der Liste und der Kanban-Ansicht und grenzt beide auf einen
 * Kunden ein. Die Auswahl lebt in der Adresse (``?customer=``), wie jeder
 * andere Filter der Liste (F1-40): Sie wandert zwischen Liste und Kanban
 * mit, laesst sich als Lesezeichen behalten, und wer das Ticketmodul neu
 * betritt, sieht wieder alle Kunden. Eine Auswahl, die sich still ueber den
 * Tag haelt, fuehrt frueher oder spaeter zu der Frage, wo die Tickets der
 * anderen geblieben sind.
 *
 * Fuehrt der Mandant nur einen Kunden, gibt es nichts zu waehlen -- das
 * Feld bleibt dann aus. Steht trotzdem einer in der Adresse, erscheint es,
 * damit sich die Einschraenkung sehen und zuruecknehmen laesst.
 *
 * **Inaktive Kunden sind waehlbar.** Wer die alten Vorgaenge eines
 * ruhenden Kunden nachschlagen will, soll das koennen.
 */
export function TicketCustomerFilter({ customerId, onChange }: TicketCustomerFilterProps) {
  const { t } = useTranslation();
  const customers = useCustomerList({ limit: 2 });
  const chosen = useCustomer(customerId);

  const several = (customers.data?.pages[0]?.items.length ?? 0) > 1;
  if (!several && customerId === '') return null;

  // Bis der Name da ist, steht die Auswahl mit leerer Beschriftung im Feld
  // -- und nicht als leeres Feld: Das saehe aus, als waere nichts gewaehlt,
  // waehrend die Liste darunter schon eingegrenzt ist.
  const value: ComboboxOption | null =
    customerId === ''
      ? null
      : chosen.data
        ? customerOption(chosen.data, t(`customerStatus.${chosen.data.status}`))
        : { id: customerId, label: chosen.isError ? t('tickets.customerFilterUnknown') : '' };

  return (
    <div className="toolbar">
      <label className="field__label" htmlFor="ticket-customer-filter">
        {t('crm.customer')}
      </label>
      <CustomerCombobox
        id="ticket-customer-filter"
        value={value}
        onChange={(option) => {
          onChange(option?.id);
        }}
        clearable
        placeholder={t('tickets.customerFilterAll')}
      />
    </div>
  );
}
